<c-container class="mt-5">
  @if (currentUser(); as user) {
    <button mat-raised-button class="margin-bottom-med margin-top-med" (click)="onBackToUsersClick()"
            data-testid="back-to-users-button">
      <mat-icon>arrow_back_ios</mat-icon>
      Back to Users
    </button>
    <h1 data-testid="current-user-name-value">{{ user.FirstName }} {{ user.LastName }}</h1>

    @if (user.CausableAdmin) {
      <div class="causable-admin-badge">
        <mat-icon class="gold-icon">star</mat-icon>
        <span class="bold-text">Causable Admin (Full Access)</span>
      </div>
    }

    <c-row class="mt-4">
      <c-col md="3" class="mb-3">
        <div cListGroup>
          <button cListGroupItem
                  data-testid="general-info-tab"
                  (click)="screenState.set(screenStateEnum.ViewDetails)"
                  [active]="screenState() === screenStateEnum.ViewDetails">
            <div>
              <h5 class="mb-1">View User Details</h5>
              <p>view general details about this user</p>
            </div>
          </button>

          <button cListGroupItem
                  data-testid="view-roles-tab"
                  (click)="screenState.set(screenStateEnum.ViewRoles)"
                  [active]="screenState() === screenStateEnum.ViewRoles">
            <div>
              <h5 class="mb-1">View User Roles</h5>
              <p>view all roles assigned to this user</p>
            </div>
          </button>

          <button cListGroupItem
                  data-testid="add-roles-tab"
                  *hasPermission="PermissionTypes.ModifyAdminUsers"
                  (click)="screenState.set(screenStateEnum.AddRoles)"
                  [active]="screenState() === screenStateEnum.AddRoles">
            <div>
              <h5 class="mb-1">Add Roles or Access</h5>
              <p>assign new roles or charity access to this user</p>
            </div>
          </button>

        </div>
      </c-col>
      <c-col md="9">
        @switch (screenState()) {
          @case (screenStateEnum.ViewDetails) {
            <mat-card>
              <mat-card-header>
                <mat-card-title>View User Details</mat-card-title>
              </mat-card-header>
              <mat-card-content>
                <div>
                  <div class="detail-label">Admin Since</div>
                  <div class="detail-value"
                       data-testid="current-user-created-on">{{ user.CreatedOn | date: 'MMM d, y' }}
                  </div>
                </div>

                <div>
                  <div class="detail-label">Email Address</div>
                  <div class="detail-value" data-testid="current-user-email-value">{{ user.Email }}</div>
                </div>

                <div>
                  <div class="detail-label">Phone Number</div>
                  <div class="detail-value" data-testid="current-user-phone-number">{{ user.Phone }}</div>
                </div>

                <div>
                  <div class="detail-label">Admin Id</div>
                  <div class="detail-value" data-testid="current-user-id-value">{{ user.Id }}</div>
                </div>

                <div>
                  <div class="detail-label">Verification Code</div>
                  <div class="detail-value"
                       data-testid="current-user-verification-value">{{ user.VerificationCode }}
                  </div>
                </div>

                <div>
                  <div class="detail-label">Status</div>
                  <div class="detail-value" data-testid="current-user-status">
                    <mat-icon
                      [ngClass]="user.Active ? 'green-text' : 'red-text'">{{ user.Active ? 'check' : 'do_not_disturb' }}
                    </mat-icon>
                    <span
                      [ngClass]="user.Active ? 'green-text' : 'red-text'">{{ user.Active ? 'Active' : 'Deactivated' }}</span>
                  </div>
                </div>
              </mat-card-content>
            </mat-card>
          }
          @case (screenStateEnum.ViewRoles) {
            <!-- Cross-Game Roles Section -->
            @if (crossGameRoles().length > 0) {
              <mat-card class="mb-4">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon class="title-icon">public</mat-icon>
                    Cross-Game Roles (All Games)
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <app-role-table
                    [dataSource]="crossGameRoles()"
                    [columns]="roleColumns"
                    testIdPrefix="cross-game-role"
                    removeTooltip="Remove this role"
                    removeAriaLabel="Remove this cross-game role"
                    (remove)="onRemoveCrossGameRoleClick($event)">
                  </app-role-table>
                </mat-card-content>
              </mat-card>
            }

            <!-- Game-Specific Roles Section -->
            @if (gameSpecificRoles().length > 0) {
              <div data-testid="game-roles-section">
                @for (gameGroup of gameSpecificRoles(); track gameGroup.GameId) {
                  <mat-card class="mb-4">
                    <mat-card-header>
                      <mat-card-title>
                        <mat-icon class="title-icon">videogame_asset</mat-icon>
                        {{ gameGroup.GameName }} - Game Roles
                      </mat-card-title>
                    </mat-card-header>
                    <mat-card-content>
                      <app-role-table
                        [dataSource]="gameGroup.GameRoles"
                        [columns]="roleColumns"
                        [testIdPrefix]="'game-role-' + gameGroup.GameId"
                        removeTooltip="Remove this role"
                        removeAriaLabel="Remove this game role"
                        (remove)="onRemoveGameRoleClick($event, gameGroup.GameId)">
                      </app-role-table>
                    </mat-card-content>
                  </mat-card>
                }
              </div>
            }

            <!-- Charity Admin Roles Section -->
            @if (charityRoles().length > 0) {
              <mat-card class="mb-4">
                <mat-card-header>
                  <mat-card-title>
                    <mat-icon class="title-icon">volunteer_activism</mat-icon>
                    Charity Admin Access
                  </mat-card-title>
                </mat-card-header>
                <mat-card-content>
                  <p class="mb-3">This user has admin access to the following charities:</p>
                  <app-role-table
                    [dataSource]="charityRoles()"
                    [columns]="charityColumns"
                    testIdPrefix="charity-role"
                    removeTooltip="Remove charity admin access"
                    removeAriaLabel="Remove charity admin access"
                    (remove)="onRemoveCharityAdminClick($event)">
                  </app-role-table>
                </mat-card-content>
              </mat-card>
            }

            @if (crossGameRoles().length === 0 && gameSpecificRoles().length === 0 && charityRoles().length === 0 && !user.CausableAdmin) {
              <mat-card>
                <mat-card-content>
                  <p class="text-center">This user does not have any assigned roles.</p>
                </mat-card-content>
              </mat-card>
            }
          }
          @case (screenStateEnum.AddRoles) {
            <app-add-user-roles
              [targetUser]="user"
              (rolesAddedSuccessfully)="onRolesAddedSuccessfully()"
              (cancelled)="onAddRolesCancelled()"
              data-testid="add-user-roles-component">
            </app-add-user-roles>
          }
        }
      </c-col>
    </c-row>
  }
</c-container>
